import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { catchError, filter, map, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { closingSetupActions } from '../actions/closing-setup.actions';
import { IClosingSetup } from '../models/closing-setup.model';
import { accountingFileRefusalOf } from './accounting-file.errors';
import {
    selectClosingLaunchFinished,
    selectClosingLaunchInFlight,
    selectClosingSetupPolling,
} from '../selectors/closing-setup.selectors';
import { ClosingSetupService } from '../services/closing-setup.service';

/**
 * How often the launch screen asks again.
 *
 * Two seconds: the steps are a vendor pull and a Graph walk, so they take tens of seconds, and
 * the screen has a row that animates between answers. Tighter would buy nothing but requests.
 */
const POLL_INTERVAL_MS = 2000;

/**
 * A ceiling on the poll, so a forgotten tab does not poll for the rest of the day.
 *
 * The pipeline stamps `finishedAt` whatever the outcome, so the ordinary end is the stop condition
 * below. When a pod dies mid-pipeline that stamp never comes, but the run stops beating and the
 * backend reports it as interrupted within minutes, so the banner offers «Erneut versuchen» on its
 * own. This ceiling only guards forgotten tabs, and sits far above any real import so a long live
 * pull is followed to the end.
 */
const POLL_GIVE_UP_MS = 3 * 60 * 60_000;

/**
 * What to show the user for a failed call.
 *
 * ⚠️ The server's `messageKey` comes FIRST. `HttpErrorResponse.message` is Angular's own technical
 * string ("Http failure response for …: 409 Conflict") — useless to a trustee, and it was all this
 * returned. The backend answers a refused second start with
 * `ConflictException({ messageKey: 'CLOSING_SETUP_ALREADY_RUNNING' })`, and that key never reached
 * the screen: the user manual promised a sentence the frontend could not produce
 * (Code-Review Runde 2, R2-01).
 *
 * The key is returned as-is; translating it is the view's job, which is where the i18n pipe lives.
 */
export function messageOf(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
        return String(error);
    }

    const body: unknown = error.error;
    const key = (body as { messageKey?: unknown } | null)?.messageKey;

    return typeof key === 'string' && key.length > 0 ? key : error.message;
}

/**
 * The Setup-Wizard's effects (REV-21354).
 *
 * The poll is the only interesting one. It runs on `Start Closing Success`, and on `Watch Launch` when
 * a read finds a launch still in flight (a reload, or the overview the wizard hands over to). A read
 * of a finished closing starts nothing: the navigation gate reads the same route on every entry.
 */
@Injectable()
export class ClosingSetupEffects {
    public constructor(
        private readonly actions$: Actions,
        private readonly store: Store,
        private readonly service: ClosingSetupService,
    ) {}

    public readonly loadSetup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.loadSetup),
            // `switchMap`: only the newest closing's answer matters, and a slower earlier one would
            // otherwise land after it. The reducer drops stale ids too — belt and braces, because this
            // one decides which request even survives.
            switchMap(({ projectId }) =>
                this.service.read(projectId).pipe(
                    map((setup: IClosingSetup | null) => closingSetupActions.loadSetupSuccess({ projectId, setup })),
                    catchError((error: unknown) =>
                        of(closingSetupActions.loadSetupFailure({ projectId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly refreshSetup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.refreshSetup),
            switchMap(({ projectId }) =>
                this.service.read(projectId).pipe(
                    map((setup: IClosingSetup | null) => closingSetupActions.refreshSetupSuccess({ projectId, setup })),
                    catchError((error: unknown) =>
                        of(closingSetupActions.refreshSetupFailure({ projectId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    public readonly startClosing$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.startClosing),
            switchMap(({ projectId, request }) =>
                this.service.start(projectId, request).pipe(
                    map((setup: IClosingSetup) => closingSetupActions.startClosingSuccess({ projectId, setup })),
                    catchError((error: unknown) =>
                        of(closingSetupActions.startClosingFailure({ projectId, error: messageOf(error) })),
                    ),
                ),
            ),
        ),
    );

    /**
     * REV-22007 — the trial-balance upload. `switchMap`: a second file picked while the first is still
     * on its way REPLACES it — only the last pick is what the trustee means, and the server stores a
     * file only when it accepts it, so an abandoned request leaves at most an unreferenced upload.
     */
    public readonly uploadAccountingFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.uploadAccountingFile),
            switchMap(({ projectId, file }) =>
                this.service.uploadAccountingFile(projectId, file).pipe(
                    map((preview) => closingSetupActions.uploadAccountingFileSuccess({ projectId, preview })),
                    catchError((error: unknown) =>
                        of(
                            closingSetupActions.uploadAccountingFileFailure({
                                projectId,
                                refusal: accountingFileRefusalOf(error),
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Follows a running pipeline until it comes to rest.
     *
     * It emits `Refresh Setup`, not `Load Setup`: the gate's read resets the closing to "unknown"
     * until it answers, and the navigation reads that as "not started" (REV-22527).
     *
     * ⚠️ `timer(POLL_INTERVAL_MS, …)` starts DELAYED on purpose: `Start Closing Success` has just put
     * the server's own answer into the store, so an immediate tick would re-ask for what we hold.
     */
    public readonly pollLaunch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.startClosingSuccess, closingSetupActions.watchLaunch),
            switchMap(({ projectId }) =>
                timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS).pipe(
                    map(() => closingSetupActions.refreshSetup({ projectId })),
                    takeUntil(timer(POLL_GIVE_UP_MS)),
                    // Leaving the screen, or a second launch, ends this one. Without it two overlapping
                    // timers would poll the same closing.
                    takeUntil(
                        this.actions$.pipe(
                            ofType(closingSetupActions.stopPolling, closingSetupActions.startClosing),
                        ),
                    ),
                ),
            ),
        ),
    );

    /**
     * Picks the poll up again when a read finds a launch still running and nobody follows it — after
     * a reload in the middle of the import, the banner must keep moving.
     *
     * `polling` in state is what keeps this from restarting the timer on every tick: the poll's own
     * reads arrive while it is set.
     */
    public readonly resumePollingWhenInFlight$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.loadSetupSuccess),
            withLatestFrom(this.store.select(selectClosingSetupPolling), this.store.select(selectClosingLaunchInFlight)),
            filter(([, polling, inFlight]) => !polling && inFlight),
            map(([{ projectId }]) => closingSetupActions.watchLaunch({ projectId })),
        ),
    );

    /**
     * Stops the poll once the pipeline has stamped itself finished.
     *
     * Separate from the timer rather than a `takeWhile` inside it: the stop is a STATE fact, and
     * reading it from the store keeps the rule in one place — the same selector the screen uses to
     * decide it is done.
     */
    public readonly stopPollingWhenFinished$ = createEffect(() =>
        this.actions$.pipe(
            ofType(closingSetupActions.loadSetupSuccess, closingSetupActions.refreshSetupSuccess),
            withLatestFrom(this.store.select(selectClosingLaunchFinished)),
            filter(([, finished]) => finished),
            map(([{ projectId }]) => closingSetupActions.stopPolling({ projectId })),
        ),
    );
}
